import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export type OfferingCode =
  | 'AI_TRAVEL_STYLING'
  | 'AI_EVENT_STYLING'
  | 'AI_PERSONAL_STYLING'
  | 'EXPERT_TRAVEL_BASIC'
  | 'EXPERT_TRAVEL_PREMIUM'
  | 'EXPERT_EVENT_BASIC'
  | 'EXPERT_EVENT_PREMIUM'
  | 'EXPERT_PERSONAL_BASIC'
  | 'EXPERT_PERSONAL_PREMIUM';
export type OfferingCategory = 'TRAVEL' | 'EVENT' | 'PERSONAL';

export interface Offering {
  code: OfferingCode;
  name: string;
  enabled: boolean;
  currency: string;
  amountInr: number;
  category: OfferingCategory;
  description?: string;
}

export interface OfferingsConfig {
  offerings: Offering[];
}

export const DEFAULT_OFFERINGS: Offering[] = [
  { code: 'AI_TRAVEL_STYLING', name: 'AI Travel Styling', enabled: true, currency: 'INR', amountInr: 999, category: 'TRAVEL' },
  { code: 'AI_EVENT_STYLING', name: 'AI Event Styling', enabled: true, currency: 'INR', amountInr: 999, category: 'EVENT' },
  { code: 'AI_PERSONAL_STYLING', name: 'AI Personal Styling', enabled: true, currency: 'INR', amountInr: 999, category: 'PERSONAL' },
  { code: 'EXPERT_TRAVEL_BASIC', name: 'Expert Travel Styling (Basic)', enabled: true, currency: 'INR', amountInr: 4999, category: 'TRAVEL' },
  { code: 'EXPERT_TRAVEL_PREMIUM', name: 'Expert Travel Styling (Premium)', enabled: true, currency: 'INR', amountInr: 6999, category: 'TRAVEL' },
  { code: 'EXPERT_EVENT_BASIC', name: 'Expert Event Styling (Basic)', enabled: true, currency: 'INR', amountInr: 4999, category: 'EVENT' },
  { code: 'EXPERT_EVENT_PREMIUM', name: 'Expert Event Styling (Premium)', enabled: true, currency: 'INR', amountInr: 6999, category: 'EVENT' },
  { code: 'EXPERT_PERSONAL_BASIC', name: 'Expert Personal Styling (Basic)', enabled: true, currency: 'INR', amountInr: 4999, category: 'PERSONAL' },
  { code: 'EXPERT_PERSONAL_PREMIUM', name: 'Expert Personal Styling (Premium)', enabled: true, currency: 'INR', amountInr: 6999, category: 'PERSONAL' }
];

export const getOfferings = async (): Promise<OfferingsConfig> => {
  const defaults = DEFAULT_OFFERINGS;

  const snap = await getDoc(doc(db, 'offerings', 'current'));
  if (!snap.exists()) {
    return { offerings: defaults };
  }
  const data = snap.data() as any;
  const raw = Array.isArray(data.offerings) ? (data.offerings as any[]) : [];

  const normalized = raw
    .map((o) => {
      const codeRaw = typeof o?.code === 'string' ? o.code : '';
      const mappedCode =
        codeRaw === 'AI_TRIP_REPORT' ||
        codeRaw === 'AI_BASIC' ||
        codeRaw === 'AI_PREMIUM' ||
        codeRaw === 'AI_STYLING'
          ? 'AI_TRAVEL_STYLING'
          : codeRaw === 'EXPERT_TRIP_REPORT' || codeRaw === 'EXPERT_BASIC'
            ? 'EXPERT_TRAVEL_BASIC'
            : codeRaw === 'EXPERT_PREMIUM'
              ? 'EXPERT_TRAVEL_PREMIUM'
              : codeRaw;

      if (!defaults.some((d) => d.code === mappedCode)) {
        return null;
      }

      const derivedCategory: OfferingCategory = mappedCode.includes('_EVENT_')
        ? 'EVENT'
        : mappedCode.includes('_PERSONAL_')
          ? 'PERSONAL'
          : 'TRAVEL';

      return {
        code: mappedCode as OfferingCode,
        name: typeof o?.name === 'string' ? o.name : defaults.find((d) => d.code === mappedCode)?.name || mappedCode,
        enabled: typeof o?.enabled === 'boolean' ? o.enabled : true,
        currency: typeof o?.currency === 'string' ? o.currency : 'INR',
        amountInr:
          typeof o?.amountInr === 'number'
            ? mappedCode.startsWith('AI_') && o.amountInr === 1999
              ? defaults.find((d) => d.code === mappedCode)?.amountInr || 0
              : o.amountInr
            : defaults.find((d) => d.code === mappedCode)?.amountInr || 0,
        category: derivedCategory,
        description: typeof o?.description === 'string' ? o.description : undefined
      } satisfies Offering;
    })
    .filter(Boolean) as Offering[];

  const byCode = new Map<OfferingCode, Offering>();
  normalized.forEach((o) => byCode.set(o.code, o));
  const merged = defaults.map((d) => byCode.get(d.code) || d);
  return { offerings: merged };
};

export const setOfferings = async (config: OfferingsConfig): Promise<void> => {
  await setDoc(doc(db, 'offerings', 'current'), config, { merge: true });
};
